import type { PeriodInfo } from "@core/period-info"
import { getSelectedPeriodInfo } from "@core/period-info"
import type { PriceType } from "@core/types"
import type { CloudBluePeriod } from "@gatsby-plugin-definitions/fragments/CloudBluePeriod"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import { getDetailedPriceData } from "@gatsby-plugin-generic-page/components/DetailedPrice/helpers/getDetailedPriceData"
import { getDetailedStaticPriceData } from "@gatsby-plugin-generic-page/components/DetailedStaticPrice/helpers/getDetailedStaticPriceData"
import type { OptionMapping } from "@gatsby-plugin-marketplace/fragments/optionMapping"
import type { PriceData } from "@ionos-web-design-system/react"
import { get } from "lodash"
import type { ComparableProduct } from "~/fragments/comparableProduct"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import isEmpty from "~/lib/isEmpty"
import type { PeriodName } from "~/types/squidex"
import findProductOptions from "./findProductOptions"

/**
 * Returns UDS `PriceData` for a product in the comparison table, or `null` when price cannot be resolved.
 *
 * CloudBlue plan resolution order:
 * 1. `currentPlanSettings` — when it already belongs to this product's sale config
 * 2. Closest matching option from `findProductOptions`
 * 3. Default plan from the sale configuration
 *
 * @param comparableProduct - Product from the comparison table
 * @param currentPriceType - Net or gross price type
 * @param currentPlanSettings - Active option mapping, or `null` when none selected
 * @param saleConfiguration - Sale config of the currently selected product
 * @param selectedPeriod - Billing period chosen by the user
 *
 * @returns `PriceData` for the UDS price component, or `null`
 */
export default function getProductPriceData(
  comparableProduct: ComparableProduct,
  currentPriceType: PriceType,
  currentPlanSettings: OptionMapping | null,
  saleConfiguration?: SaleConfiguration,
  selectedPeriod?: PeriodName
): PriceData | null {
  const currentPlanSettingsPeriods: CloudBluePeriod[] = get(
    currentPlanSettings,
    "cloudBluePlan[0].flatData.periods",
    []
  )

  const currentPlanPeriodInfo = !isEmpty(currentPlanSettingsPeriods)
    ? getSelectedPeriodInfo(currentPlanSettingsPeriods, selectedPeriod)
    : undefined

  const currentProductHasMultipleCommitments =
    currentPlanPeriodInfo?.hasMultipleCommitments()

  const {
    flatData: { saleConfigurations, staticPrice },
  } = comparableProduct

  // Static price takes priority — use it directly when available.
  if (!isEmpty(staticPrice)) {
    const period = staticPrice[0].flatData.period
    const result = getDetailedStaticPriceData(
      staticPrice,
      period,
      currentPriceType
    )

    return result?.data ?? null
  }

  // Require at least one sale configuration with a default CloudBlue plan.
  const hasPlan = !!get(
    comparableProduct,
    "flatData.saleConfigurations[0].flatData.defaultCloudBluePlan[0].id"
  )

  if (isEmpty(saleConfigurations) || !hasPlan) {
    return null
  }

  let product: CloudBluePlan["flatData"] | undefined
  let productOptions: OptionMapping | null | undefined

  if (
    !isEmpty(currentPlanSettings) &&
    !isEmpty(saleConfiguration) &&
    saleConfiguration.id === saleConfigurations[0].id
  ) {
    // currentPlanSettings already belongs to this product — use it directly.
    product = get(currentPlanSettings, "cloudBluePlan[0].flatData")
  } else {
    // Find the best matching option, or fall back to the default plan.
    productOptions = findProductOptions(
      currentPlanSettings,
      saleConfigurations[0]
    )

    const productOptionsPlanData: CloudBluePlan["flatData"] | undefined = get(
      productOptions,
      "cloudBluePlan[0].flatData"
    )

    const productDefaultPlanData: CloudBluePlan["flatData"] | undefined = get(
      saleConfigurations[0],
      "flatData.defaultCloudBluePlan[0].flatData"
    )

    product = productOptionsPlanData || productDefaultPlanData
  }

  if (!product) {
    return null
  }

  // Use selectedPeriod if set, otherwise fall back to the option's default period.
  const periodInfo: PeriodInfo | undefined = getSelectedPeriodInfo(
    product.periods,
    selectedPeriod || productOptions?.period?.period
  )

  // When the current product has multiple commitments, only show product prices that also have multiple commitments
  // otherwise, the comparison would be misleading.
  const productHasMultipleCommitments =
    !isEmpty(periodInfo) &&
    periodInfo.hasMultipleCommitments() === currentProductHasMultipleCommitments

  if (
    typeof currentProductHasMultipleCommitments === "boolean" &&
    !productHasMultipleCommitments
  ) {
    return null
  }

  if (!periodInfo) {
    return null
  }

  const customPriceText: string | undefined = get(
    saleConfigurations[0],
    "flatData.customPriceBox.priceText"
  )

  if (customPriceText) {
    return {
      prelines: {
        primary: {
          preline: customPriceText,
        },
      },
    }
  }

  return getDetailedPriceData(currentPriceType, periodInfo)
}
